<script setup lang="ts">
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppSlidePanel, AppLinkButton, AppButton } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  FlightsFilterEvents,
  FlightsFilters,
} from './FlightsFilters.model';
import FlightsFiltersLogic from './FlightsFiltersLogic.vue';

interface FlightsFiltersPanelProps {
  isOpen: boolean;
  headerText: string;
  data: FlightsFilters | null;
  shouldUpdateOnClick?: boolean;
}
const props = defineProps<FlightsFiltersPanelProps>();

interface FlightsFiltersPanelEmits {
  (eventName: 'action', context: FlightsFilterEvents): void;
  (eventName: 'apply-all-filters', context: FlightsFilterEvents[]): void;
  (eventName: 'reset-all-filters', ingredientId: IngredientId): void;
  (eventName: 'close'): void;
}
const emit = defineEmits<FlightsFiltersPanelEmits>();

const browserStore = useBrowserStore();

let filterEventsActions: FlightsFilterEvents[] = [];

function triggerAction(action: FlightsFilterEvents): void {
  emit('action', action);
}

function storeNewActions(actions: FlightsFilterEvents[]): void {
  filterEventsActions = actions;
}

function closeFiltersPanel(): void {
  emit('close');
}

function applyAllFilters(): void {
  emit('apply-all-filters', filterEventsActions);
}

function resetAllFilters(): void {
  if (props?.data?.ingredientId) {
    filterEventsActions.length = 0;
    emit('reset-all-filters', props.data.ingredientId);
  }
}
</script>

<template>
  <AppSlidePanel
    :is-open="props.isOpen"
    direction="right"
    :width="!browserStore.isLargerThan1199px ? '100%' : '33.33%'"
    padding="0"
    should-show-close-button
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="closeFiltersPanel"
    @confirm="props.shouldUpdateOnClick ? closeFiltersPanel : applyAllFilters"
  >
    <template #title>
      {{ props.headerText }}
    </template>

    <template #body>
      <div class="flights-filters-panel-body">
        <FlightsFiltersLogic
          v-if="props.data"
          :data="props.data"
          :should-update-on-click="props.shouldUpdateOnClick"
          @trigger-action="triggerAction"
          @new-actions="storeNewActions"
        />
      </div>
    </template>
    <template #footer>
      <div class="flights-filters-panel-footer">
        <template v-if="props.shouldUpdateOnClick">
          <AppLinkButton
            :text="$t('filter.reset_filters')"
            size="m"
            @click="resetAllFilters"
          />
          <AppButton
            type="primary"
            state="enabled"
            size="medium"
            @click="applyAllFilters"
          >
            {{ $t('planner.done') }}
          </AppButton>
        </template>
        <template v-else>
          <AppButton
            type="primary"
            state="enabled"
            size="large"
            :wide="true"
            @click="closeFiltersPanel"
          >
            {{ $t('planner.show_results') }}
          </AppButton>
        </template>
      </div>
    </template>
  </AppSlidePanel>
</template>

<style scoped lang="scss">
.flights-filters-panel-body {
  padding-left: var(--spacing-16);
}

.flights-filters-panel-footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-16);
}
</style>
